/**
 * Word Document Search Service
 *
 * Provides document-wide search capabilities with 2-tier fallback strategy.
 * Replaces the old searchParagraph-based approach to fix Word Desktop 256-char limit.
 *
 * Key Changes (GPD-XXX):
 * - Document-wide search using searchPhrase (<250 chars)
 * - Occurrence-based selection using searchPhraseOccurrence
 * - 2-tier fallback: LENIENT (ignorePunct, ignoreSpace) → STRICT (exact match)
 * - No searchParagraph or tracked paragraph ranges
 */

/* global Word */

import { createLogger } from "../../../utils/logger";

const log = createLogger("WordDocumentSearch");

/**
 * Search options for lenient text matching
 * Ignores punctuation and spacing differences
 */
export const LENIENT_SEARCH_OPTIONS = {
  matchCase: true,
  matchWholeWord: false,
  ignorePunct: true,
  ignoreSpace: true,
};

/**
 * Search options for strict text matching (fallback)
 * Requires exact match including punctuation and spacing
 */
export const STRICT_SEARCH_OPTIONS = {
  matchCase: true,
  matchWholeWord: false,
  ignorePunct: false,
  ignoreSpace: false,
};

/**
 * Search result with metadata
 */
export interface SearchResult {
  /** The Word RangeCollection containing all found occurrences */
  results: Word.RangeCollection;

  /** Total number of occurrences found */
  totalOccurrences: number;

  /** Whether fallback (strict) search was used */
  usedFallback: boolean;
}

/**
 * Search entire document for a phrase with 2-tier fallback
 *
 * Tier 1: LENIENT search (ignorePunct, ignoreSpace)
 * Tier 2: STRICT search (exact match) - if lenient finds nothing
 *
 * @param context - Word request context
 * @param searchPhrase - The phrase to search for (<250 chars, guaranteed by backend)
 * @param options - Optional search configuration
 * @returns Promise of SearchResult with loaded items
 * @throws Error if searchPhrase exceeds 255 characters (Word Desktop limit)
 */
export async function searchDocument(
  context: Word.RequestContext,
  searchPhrase: string,
  options?: {
    /** Skip fallback and only try lenient search */
    skipFallback?: boolean;
  }
): Promise<SearchResult> {
  // Validate search phrase length (Word Desktop 256-char limit)
  if (searchPhrase.length > 255) {
    const errorMsg =
      `Search phrase exceeds Word Desktop 255-character limit (${searchPhrase.length} chars). ` +
      `Please send this error message to our support team using the feedback thumb down button.`;
    log.error("❌ Search phrase length validation failed", {
      length: searchPhrase.length,
      phrase: searchPhrase.substring(0, 50) + "...",
    });
    throw new Error(errorMsg);
  }

  log.debug("Searching document", {
    phraseLength: searchPhrase.length,
    phrasePreview: searchPhrase.substring(0, 50) + (searchPhrase.length > 50 ? "..." : ""),
  });

  // TIER 1: Lenient search (primary approach)
  const lenientResults = context.document.body.search(searchPhrase, LENIENT_SEARCH_OPTIONS);
  lenientResults.load("items");
  await context.sync();

  if (lenientResults.items.length > 0) {
    log.debug("✅ Found with LENIENT search", {
      occurrences: lenientResults.items.length,
    });
    return {
      results: lenientResults,
      totalOccurrences: lenientResults.items.length,
      usedFallback: false,
    };
  }

  // TIER 2: Strict search (fallback)
  if (options?.skipFallback) {
    log.warn("⚠️ LENIENT search found nothing, skipping fallback as requested");
    return {
      results: lenientResults,
      totalOccurrences: 0,
      usedFallback: false,
    };
  }

  log.warn("⚠️ LENIENT search found nothing, trying STRICT fallback", {
    searchOptions: STRICT_SEARCH_OPTIONS,
  });

  const strictResults = context.document.body.search(searchPhrase, STRICT_SEARCH_OPTIONS);
  strictResults.load("items");
  await context.sync();

  if (strictResults.items.length > 0) {
    log.info("✅ Found with STRICT fallback search", {
      occurrences: strictResults.items.length,
    });
    return {
      results: strictResults,
      totalOccurrences: strictResults.items.length,
      usedFallback: true,
    };
  }

  // Both searches failed
  log.error("❌ Search phrase not found with LENIENT or STRICT search", {
    searchPhrase: searchPhrase.substring(0, 100) + (searchPhrase.length > 100 ? "..." : ""),
    phraseLength: searchPhrase.length,
  });

  return {
    results: strictResults,
    totalOccurrences: 0,
    usedFallback: true,
  };
}

/**
 * Search for a phrase within a specific paragraph by index
 *
 * Uses same 2-tier fallback strategy as document-wide search (LENIENT → STRICT).
 * Used for EDIT operations when paragraphId is provided to scope the search.
 *
 * @param context - Word request context
 * @param paragraphIndex - 0-based ADJUSTED index (already adjusted for INSERT/DELETE via tracker)
 * @param searchPhrase - The phrase to search for (<250 chars)
 * @param options - Optional search configuration
 * @returns Promise of SearchResult scoped to the paragraph
 * @throws Error if paragraphIndex is out of bounds
 * @throws Error if searchPhrase exceeds 255 characters
 */
export async function searchWithinParagraph(
  context: Word.RequestContext,
  paragraphIndex: number,
  searchPhrase: string,
  options?: {
    /** Skip fallback and only try lenient search */
    skipFallback?: boolean;
  }
): Promise<SearchResult> {
  // Validate search phrase length (Word Desktop 256-char limit)
  if (searchPhrase.length > 255) {
    const errorMsg =
      `Search phrase exceeds Word Desktop 255-character limit (${searchPhrase.length} chars). ` +
      `Please send this error message to our support team using the feedback thumb down button.`;
    log.error("❌ Search phrase length validation failed", {
      length: searchPhrase.length,
      phrase: searchPhrase.substring(0, 50) + "...",
    });
    throw new Error(errorMsg);
  }

  // Validate searchPhrase is not empty (Word API rejects empty string search)
  if (searchPhrase.length === 0) {
    throw new Error(
      `Search phrase cannot be empty. Use direct paragraph insertion for empty paragraph edits.`
    );
  }

  // Validate paragraph index is non-negative
  if (paragraphIndex < 0) {
    throw new Error(`paragraphId must be >= 0, got ${paragraphIndex}`);
  }

  // Load all paragraphs to access the target
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();

  // Validate paragraph index is within bounds
  if (paragraphIndex >= paragraphs.items.length) {
    throw new Error(
      `paragraphId ${paragraphIndex} out of bounds (document has ${paragraphs.items.length} paragraphs)`
    );
  }

  // Get the target paragraph's range
  const targetParagraph = paragraphs.items[paragraphIndex];
  const paragraphRange = targetParagraph.getRange("Whole");

  log.debug("Searching within paragraph", {
    paragraphIndex,
    phraseLength: searchPhrase.length,
    phrasePreview: searchPhrase.substring(0, 50) + (searchPhrase.length > 50 ? "..." : ""),
  });

  // TIER 1: Lenient search within paragraph (primary approach)
  const lenientResults = paragraphRange.search(searchPhrase, LENIENT_SEARCH_OPTIONS);
  lenientResults.load("items");
  await context.sync();

  if (lenientResults.items.length > 0) {
    log.debug("✅ Found in paragraph with LENIENT search", {
      paragraphIndex,
      occurrences: lenientResults.items.length,
    });
    return {
      results: lenientResults,
      totalOccurrences: lenientResults.items.length,
      usedFallback: false,
    };
  }

  // TIER 2: Strict search (fallback)
  if (options?.skipFallback) {
    log.warn("⚠️ LENIENT search found nothing in paragraph, skipping fallback as requested", {
      paragraphIndex,
    });
    return {
      results: lenientResults,
      totalOccurrences: 0,
      usedFallback: false,
    };
  }

  log.warn("⚠️ LENIENT search found nothing in paragraph, trying STRICT fallback", {
    paragraphIndex,
    searchOptions: STRICT_SEARCH_OPTIONS,
  });

  const strictResults = paragraphRange.search(searchPhrase, STRICT_SEARCH_OPTIONS);
  strictResults.load("items");
  await context.sync();

  if (strictResults.items.length > 0) {
    log.info("✅ Found in paragraph with STRICT fallback search", {
      paragraphIndex,
      occurrences: strictResults.items.length,
    });
    return {
      results: strictResults,
      totalOccurrences: strictResults.items.length,
      usedFallback: true,
    };
  }

  // Both searches failed
  log.error("❌ Search phrase not found in paragraph with LENIENT or STRICT search", {
    paragraphIndex,
    searchPhrase: searchPhrase.substring(0, 100) + (searchPhrase.length > 100 ? "..." : ""),
    phraseLength: searchPhrase.length,
  });

  return {
    results: strictResults,
    totalOccurrences: 0,
    usedFallback: true,
  };
}

/**
 * Select a specific occurrence from search results
 *
 * Validates that the requested occurrence exists and returns the corresponding range.
 * Throws descriptive errors for debugging.
 *
 * @param searchResults - Word RangeCollection from searchDocument()
 * @param occurrenceNumber - Which occurrence to select (1-indexed)
 * @param searchPhrase - The search phrase (for error messages)
 * @param editId - Edit ID (for error messages)
 * @returns The selected Word.Range
 * @throws Error if occurrence number is invalid or exceeds found results
 */
export function selectOccurrence(
  searchResults: Word.RangeCollection,
  occurrenceNumber: number,
  searchPhrase: string,
  editId: string
): Word.Range {
  const totalOccurrences = searchResults.items.length;

  // Validate occurrence number >= 1
  if (occurrenceNumber < 1) {
    throw new Error(
      `Edit ${editId}: Invalid searchPhraseOccurrence ${occurrenceNumber}. ` +
        `Occurrence numbers are 1-indexed and must be >= 1.`
    );
  }

  // Validate occurrence doesn't exceed found count
  if (occurrenceNumber > totalOccurrences) {
    throw new Error(
      `Edit ${editId}: searchPhraseOccurrence is ${occurrenceNumber} ` +
        `but only ${totalOccurrences} occurrence(s) of "${searchPhrase}" found in document. ` +
        `Backend calculated ${occurrenceNumber}, but document has changed or calculation was incorrect. ` +
        `Expected to find at least ${occurrenceNumber} occurrences.`
    );
  }

  // Convert to 0-indexed for array access
  const index = occurrenceNumber - 1;
  const selectedRange = searchResults.items[index];

  log.debug("✅ Selected occurrence", {
    editId,
    occurrenceNumber,
    totalOccurrences,
    usedIndex: index,
  });

  return selectedRange;
}

/**
 * Search for target text within a container range
 *
 * Used for replace operations to find targetText within the selected searchPhrase occurrence.
 *
 * @param containerRange - The range to search within (selected searchPhrase occurrence)
 * @param targetText - Text to find within the range
 * @param matchWholeWord - Whether to match whole words (usually false)
 * @param context - Word request context
 * @returns Promise of RangeCollection with loaded items
 */
export async function searchWithinRange(
  containerRange: Word.Range,
  targetText: string,
  matchWholeWord: boolean,
  context: Word.RequestContext
): Promise<Word.RangeCollection> {
  const results = containerRange.search(targetText, {
    matchCase: true,
    matchWholeWord: matchWholeWord,
    ignorePunct: false,
    ignoreSpace: false,
  });

  results.load("items");
  await context.sync();

  return results;
}
